import React, { useState, useEffect } from 'react';
import { Accordion, Card, Table, OverlayTrigger, Tooltip, Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateBillPaidStatus, updateBillDetails, deleteBill } from '../../services/BillService';
import { debugError } from '../../utils/debugUtils';
import { faChevronDown, faChevronUp, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import PaidBillDropdownButton from './PaidBillDropdownButton'; // Import your dropdown button component

const PaidBillsGroup = ({ groupTitle, bills, onUpdatePaidDate, bankAccountId, onDelete, showAllBills = false, bankAccounts = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [editedBill, setEditedBill] = useState({});
  const [applyToAll, setApplyToAll] = useState(false);
  const [deleteSeries, setDeleteSeries] = useState(false);

  // Mobile detection state
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Phone detection: either portrait (width <= 480) or landscape (height <= 480 && width <= 896)
      const isPhonePortrait = width <= 480;
      const isPhoneLandscape = height <= 480 && width <= 896;
      setIsMobilePortrait(isPhonePortrait || isPhoneLandscape);
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const handleDropdownButtonClick = (billId, title) => {
    const billToUpdate = bills.find(bill => bill.id === billId);
    if (!billToUpdate) return;

    if (title === 'Unpaid') {
      const updatedBill = {
        ...billToUpdate,
        paid: false,
        paid_date: null,
        name: billToUpdate.name,
        amount: billToUpdate.amount,
        bank_account: billToUpdate.bank_account,
        due_date: billToUpdate.due_date,
        frequency: billToUpdate.frequency || 'by_date',
        description: billToUpdate.description || null,
        notes: billToUpdate.notes || null,
        due_day: billToUpdate.due_day || null,
        num_bills: billToUpdate.num_bills || 1
      };

      updateBillPaidStatus(billId, false, null, updatedBill)
        .then((updatedBill) => {
          onUpdatePaidDate(updatedBill);
        })
        .catch((error) => {
          debugError('Error updating bill status:', error.name);
        });
    } else if (title === 'Update') {
      setSelectedBill(billToUpdate);
      setEditedBill({
        name: billToUpdate.name,
        amount: billToUpdate.amount,
        due_date: billToUpdate.due_date,
        paid_date: billToUpdate.paid_date,
        bill_series_id: billToUpdate.bill_series_id,
        bank_account: billToUpdate.bank_account,
        frequency: billToUpdate.frequency || 'by_date',
        description: billToUpdate.description || null,
        notes: billToUpdate.notes || null,
        due_day: billToUpdate.due_day || null,
        num_bills: billToUpdate.num_bills || 1
      });
      setShowUpdateModal(true);
    } else if (title === 'Delete') {
      setSelectedBill(billToUpdate);
      setShowDeleteModal(true);
    }
  };

  const handleSaveChanges = async () => {
    try {
      // Check if trying to apply to all but bill_series_id is missing
      if (applyToAll && (!selectedBill.bill_series_id || typeof selectedBill.bill_series_id !== 'string' || selectedBill.bill_series_id.trim() === '')) {
        setApplyToAll(false);
        console.warn("Cannot apply to all bills in series - this bill is not part of a series");
        alert("Changes cannot be applied to following bills.");
        return;
      }

      // Make sure all required fields are included
      const completeEditedBill = {
        ...selectedBill,
        ...editedBill,
        name: editedBill.name || selectedBill.name,
        amount: editedBill.amount || selectedBill.amount,
        due_date: editedBill.due_date || selectedBill.due_date,
        bank_account: editedBill.bank_account || selectedBill.bank_account,
        frequency: selectedBill.frequency || 'by_date',
        due_day: selectedBill.due_day,
        num_bills: selectedBill.num_bills || 1,
        notes: selectedBill.notes || null,
        description: selectedBill.description || null
      };
      
      const updatedBill = await updateBillDetails(
        selectedBill.id, 
        completeEditedBill, 
        applyToAll ? selectedBill.bill_series_id : null
      );
      onUpdatePaidDate(updatedBill);
      setShowUpdateModal(false);
      setSelectedBill(null);
      setApplyToAll(false);
    } catch (error) {
      debugError('Error updating bill details:', error.name);
      alert('Failed to update bill. Please try again.');
    }
  };

  const handleDelete = async () => {
    try {
      // Call the proper delete handler passed from parent
      if (onDelete) {
        await onDelete(selectedBill.id, deleteSeries ? selectedBill.bill_series_id : null);
      } else {
        // Fallback to direct API call if no handler provided
        await deleteBill(selectedBill.id, deleteSeries);
      }
      setShowDeleteModal(false);
      setSelectedBill(null);
      setDeleteSeries(false);
    } catch (error) {
      debugError('Error deleting bill:', error.name);
      alert('Failed to delete bill. Please try again.');
    }
  };

  // Determine tooltip text based on the group title
  const getTooltipText = () => {
    if (groupTitle === "Paid This Week") {
      return "All bills that were paid during the current week, regardless of their due date";
    } else if (groupTitle === "Next Week Bills Already Paid") {
      return "Bills that are due next week but have already been paid";
    } else {
      return "Bills that have been marked as paid";
    }
  };

  return (
    <>
      <Card>
        <Card.Header onClick={toggleAccordion} style={{ cursor: 'pointer' }}>
          <h5 className="d-flex justify-content-between align-items-center">
            <span>
              {groupTitle}
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip id={`tooltip-${groupTitle.replace(/\s+/g, '-').toLowerCase()}`}>{getTooltipText()}</Tooltip>}
              >
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  style={{ 
                    marginLeft: '8px', 
                    fontSize: '0.8em', 
                    opacity: 0.7,
                    cursor: 'help'
                  }} 
                  onClick={(e) => e.stopPropagation()}
                />
              </OverlayTrigger>
            </span>
            <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
          </h5>
        </Card.Header>
        <Accordion.Collapse in={isOpen}>
          <Card.Body>
            <Table striped style={{ textAlign: 'center', verticalAlign: 'middle' }}>
              <thead>
                <tr>
                  {isMobilePortrait ? (
                    <>
                      <th style={{ textAlign: 'left', width: 'calc(100% - 80px)' }}>Bill Details</th>
                      <th style={{ width: '80px' }}>Actions</th>
                    </>
                  ) : (
                    <>
                      <th>Bill</th>
                      <th>Amount</th>
                      <th>Due Date</th>
                      <th>Date Paid</th>
                      {showAllBills && <th>Bank Account</th>}
                      <th></th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {bills && bills.length > 0 ? (
                  bills.map((bill) => {
                    const isPaid = bill.paid; // Get the paid status
                    const isOverdue = new Date(bill.due_date) < new Date(); // Check if the bill is overdue
                    const getBankAccountName = () => {
                      return bankAccounts.find(ba => ba.id === bill.bank_account)?.nickname || 'Unknown Account';
                    };

                    return (
                      <tr 
                        key={bill.id} 
                        className={isMobilePortrait ? 'mobile-portrait-bill-row' : ''}
                        style={{ backgroundColor: isPaid ? '#e0e0e0' : 'inherit', color: isPaid ? '#a0a0a0' : 'inherit' }}
                      >
                        {isMobilePortrait ? (
                          <>
                            <td className="mobile-portrait-main-info">
                              <div className="mobile-bill-name-row">
                                <div className="mobile-bill-name">{bill.name}</div>
                              </div>
                              <div className="mobile-bill-details-row">
                                <span className="mobile-bill-amount">${bill.amount}</span>
                                <span className="mobile-bill-due-date">Due: {bill.due_date}</span>
                                {bill.paid_date && <span className="mobile-bill-paid-date">Paid: {bill.paid_date}</span>}
                              </div>
                              {(showAllBills || bill.bank_account) && (
                                <div className="mobile-bill-account">{getBankAccountName()}</div>
                              )}
                            </td>
                            <td className="mobile-portrait-actions">
                              <PaidBillDropdownButton
                                billId={bill.id}
                                onClick={handleDropdownButtonClick}
                                isPaid={isPaid}
                                isOverdue={isOverdue}
                              />
                            </td>
                          </>
                        ) : (
                          <>
                            <td style={{ textAlign: 'left' }}><h5>{bill.name}</h5></td>
                            <td>${bill.amount}</td>
                            <td>{bill.due_date}</td>
                            <td>{bill.paid_date ? bill.paid_date : '-'}</td>
                            {showAllBills && <td>{getBankAccountName()}</td>}
                            <td>
                              <PaidBillDropdownButton
                                billId={bill.id}
                                onClick={handleDropdownButtonClick}
                                isPaid={isPaid}
                                isOverdue={isOverdue}
                              />
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={isMobilePortrait ? 2 : (showAllBills ? 6 : 5)}>Nothing paid yet.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Accordion.Collapse>
      </Card>

      {/* Update Modal */}
      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} size="lg" dialogClassName="mobile-responsive-modal">
        <Modal.Header closeButton>
          <Modal.Title>Update Bill</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formBillName" className="mb-3">
              <Form.Label>Bill Name</Form.Label>
              <Form.Control
                type="text"
                value={editedBill.name || ''}
                onChange={(e) => setEditedBill({ ...editedBill, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group controlId="formBillAmount" className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={editedBill.amount || ''}
                onChange={(e) => setEditedBill({ ...editedBill, amount: parseFloat(e.target.value) })}
              />
            </Form.Group>
            <Form.Group controlId="formBillDueDate" className="mb-3">
              <Form.Label>Due Date</Form.Label>
              <Form.Control
                type="date"
                value={editedBill.due_date || ''}
                onChange={(e) => setEditedBill({ ...editedBill, due_date: e.target.value })}
              />
            </Form.Group>
            <Form.Group controlId="formBillPaidDate" className="mb-3">
              <Form.Label>Paid Date</Form.Label>
              <Form.Control
                type="date"
                value={editedBill.paid_date || ''}
                onChange={(e) => setEditedBill({ ...editedBill, paid_date: e.target.value })}
              />
            </Form.Group>
            <Form.Group controlId="formBillNotes" className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editedBill.notes || ''}
                onChange={(e) => setEditedBill({ ...editedBill, notes: e.target.value })}
              />
            </Form.Group>
            {selectedBill?.bill_series_id && (
              <Form.Group controlId="formApplyToAll" className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Apply changes to all following bills in series"
                  checked={applyToAll}
                  onChange={(e) => setApplyToAll(e.target.checked)}
                />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpdateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveChanges}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} dialogClassName="mobile-responsive-modal">
        <Modal.Header closeButton>
          <Modal.Title>Delete Bill</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete "{selectedBill?.name}"?</p>
          {selectedBill?.bill_series_id && (
            <Form.Group controlId="formDeleteSeries" className="mb-3">
              <Form.Check
                type="checkbox"
                label="Delete all bills in this series from this date forward"
                checked={deleteSeries}
                onChange={(e) => setDeleteSeries(e.target.checked)}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PaidBillsGroup;
